// Copyright (c) Zammo Inc.
// Dependency-free markdown renderer for assistant chat bubbles. We can't add a markdown
// package here (markdown-it/sanitize-html are only transitive deps and package.json is
// off-limits), so this renders a safe, common subset — bold, italic, inline code, fenced
// code, links, headings, blockquotes and lists — into React nodes. Building React elements
// (never dangerouslySetInnerHTML) means text is escaped by React and there's no XSS surface;
// link hrefs are additionally restricted to safe schemes.
import React from 'react';
import styled from 'styled-components';

type Node = React.ReactNode;

const SAFE_URL = /^(https?:\/\/|mailto:|\/)/i;

// Matches, in priority order: inline code, ![image](url), [text](url), ***bolditalic***,
// ___bolditalic___, ~~strike~~, **bold**, __bold__, *italic*, _italic_. Most-specific delimiters
// come first so e.g. *** is taken before **.
const INLINE_RE =
  /(`[^`]+`)|(!\[[^\]]*?\]\([^)\s]+?\))|(\[[^\]]+?\]\([^)\s]+?\))|(\*\*\*[^*]+?\*\*\*)|(___[^_]+?___)|(~~[^~]+?~~)|(\*\*[^*]+?\*\*)|(__[^_]+?__)|(\*[^*\n]+?\*)|(_[^_\n]+?_)/;

function parseInline(text: string, kp: string): Node[] {
  const out: Node[] = [];
  let rest = text;
  let k = 0;
  while (rest.length) {
    const m = INLINE_RE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const tok = m[0];
    k += 1;
    const key = `${kp}-${k}`;
    if (tok.startsWith('`')) {
      out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('![')) {
      const im = /^!\[([^\]]*?)\]\(([^)\s]+?)\)$/.exec(tok);
      if (im && SAFE_URL.test(im[2])) {
        out.push(<img key={key} src={im[2]} alt={im[1]} />);
      } else {
        out.push(tok);
      }
    } else if (tok.startsWith('[')) {
      const lm = /^\[([^\]]+?)\]\(([^)\s]+?)\)$/.exec(tok);
      if (lm && SAFE_URL.test(lm[2])) {
        out.push(
          <a key={key} href={lm[2]} target="_blank" rel="noopener noreferrer">
            {lm[1]}
          </a>
        );
      } else {
        out.push(tok);
      }
    } else if (tok.startsWith('***') || tok.startsWith('___')) {
      out.push(
        <strong key={key}>
          <em>{parseInline(tok.slice(3, -3), key)}</em>
        </strong>
      );
    } else if (tok.startsWith('~~')) {
      out.push(<del key={key}>{parseInline(tok.slice(2, -2), key)}</del>);
    } else if (tok.startsWith('**') || tok.startsWith('__')) {
      out.push(<strong key={key}>{parseInline(tok.slice(2, -2), key)}</strong>);
    } else {
      out.push(<em key={key}>{parseInline(tok.slice(1, -1), key)}</em>);
    }
    rest = rest.slice(m.index + tok.length);
  }
  return out;
}

// Split a GFM table row into trimmed cells, honouring leading/trailing pipes and `\|` escapes.
function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '\\' && s[i + 1] === '|') {
      cur += '|';
      i += 1;
    } else if (s[i] === '|') {
      cells.push(cur.trim());
      cur = '';
    } else {
      cur += s[i];
    }
  }
  cells.push(cur.trim());
  return cells;
}

function isSeparatorRow(line: string): boolean {
  if (!line.includes('-')) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

type Align = 'left' | 'right' | 'center' | undefined;
function alignOf(cell: string): Align {
  const l = cell.startsWith(':');
  const r = cell.endsWith(':');
  if (l && r) return 'center';
  if (r) return 'right';
  if (l) return 'left';
  return undefined;
}

// Render a run of text (no fenced code) line-by-line into block elements.
function parseTextBlocks(src: string, kp: string): Node[] {
  const lines = src.split('\n');
  const out: Node[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let quote: string[] = [];

  const inlineWithBreaks = (s: string, key: string): Node[] => {
    const segs = s.split('\n');
    const nodes: Node[] = [];
    segs.forEach((seg, i) => {
      if (i > 0) nodes.push(<br key={`${key}-br${i}`} />);
      nodes.push(...parseInline(seg, `${key}-${i}`));
    });
    return nodes;
  };

  const flushPara = () => {
    if (para.length) {
      const key = `${kp}-p${out.length}`;
      out.push(<p key={key}>{inlineWithBreaks(para.join('\n'), key)}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const cur = list;
      const key = `${kp}-l${out.length}`;
      const items = cur.items.map((it, idx) => <li key={`${key}-${idx}`}>{parseInline(it, `${key}-${idx}`)}</li>);
      out.push(cur.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>);
      list = null;
    }
  };
  const flushQuote = () => {
    if (quote.length) {
      const key = `${kp}-q${out.length}`;
      out.push(<blockquote key={key}>{inlineWithBreaks(quote.join('\n'), key)}</blockquote>);
      quote = [];
    }
  };
  const flushAll = () => {
    flushPara();
    flushList();
    flushQuote();
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') {
      flushAll();
      i += 1;
      continue;
    }

    // GFM pipe table: a row line followed by a `---|---` separator row.
    if (line.includes('|') && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      flushAll();
      const header = splitRow(line);
      const aligns = splitRow(lines[i + 1]).map(alignOf);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].trim() !== '' && lines[j].includes('|')) {
        rows.push(splitRow(lines[j]));
        j += 1;
      }
      const key = `${kp}-tbl${out.length}`;
      const cellStyle = (ci: number) => (aligns[ci] ? { textAlign: aligns[ci] } : undefined);
      out.push(
        <table key={key}>
          <thead>
            <tr>
              {header.map((c, ci) => (
                <th key={`${key}-h${ci}`} style={cellStyle(ci)}>
                  {parseInline(c, `${key}-h${ci}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={`${key}-r${ri}`}>
                {header.map((_, ci) => (
                  <td key={`${key}-r${ri}-${ci}`} style={cellStyle(ci)}>
                    {parseInline(r[ci] ?? '', `${key}-r${ri}-${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      i = j;
      continue;
    }

    // Horizontal rule: a line of only --- / *** / ___ (3+).
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushAll();
      out.push(<hr key={`${kp}-hr${out.length}`} />);
      i += 1;
      continue;
    }

    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*+]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+\.\s+(.*)$/.exec(line);
    const bq = /^\s*>\s?(.*)$/.exec(line);
    const listItem = ul || ol;

    if (h) {
      flushAll();
      const lvl = Math.min(h[1].length, 6);
      const key = `${kp}-h${out.length}`;
      out.push(React.createElement(`h${lvl}`, { key }, parseInline(h[2], key)));
    } else if (listItem) {
      flushPara();
      flushQuote();
      const ordered = !ul;
      const item = listItem[1];
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push(item);
    } else if (bq) {
      flushPara();
      flushList();
      quote.push(bq[1]);
    } else {
      flushList();
      flushQuote();
      para.push(line);
    }
    i += 1;
  }
  flushAll();
  return out;
}

function parseMarkdown(src: string): Node[] {
  // Split on fenced code blocks; odd segments are code.
  const parts = src.split('```');
  const out: Node[] = [];
  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      let body = part;
      const nl = part.indexOf('\n');
      if (nl >= 0) {
        const first = part.slice(0, nl).trim();
        if (/^[a-z0-9+#-]*$/i.test(first)) body = part.slice(nl + 1); // drop language hint line
      }
      out.push(
        <pre key={`code${i}`}>
          <code>{body.replace(/\n$/, '')}</code>
        </pre>
      );
    } else if (part) {
      out.push(...parseTextBlocks(part, `t${i}`));
    }
  });
  return out;
}

const MarkdownRoot = styled.div`
  white-space: normal;
  word-break: break-word;

  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 8px;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 12px 0 6px;
    font-weight: 600;
    line-height: 1.3;
  }
  h1 {
    font-size: 1.4em;
  }
  h2 {
    font-size: 1.22em;
  }
  h3 {
    font-size: 1.1em;
  }
  h4,
  h5,
  h6 {
    font-size: 1em;
  }
  del {
    opacity: 0.7;
  }
  img {
    max-width: 100%;
    margin: 6px 0;
    border-radius: 8px;
    display: block;
  }
  hr {
    border: none;
    border-top: 1px solid hsl(220 13% 88%);
    margin: 14px 0;
  }
  ul,
  ol {
    margin: 4px 0 8px;
    padding-left: 22px;
  }
  li {
    margin: 2px 0;
  }
  li > p {
    margin: 0;
  }
  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  strong {
    font-weight: 600;
  }
  code {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.88em;
    background: hsl(220 13% 92% / 0.7);
    padding: 1px 5px;
    border-radius: 5px;
  }
  pre {
    margin: 8px 0;
    padding: 10px 12px;
    background: hsl(220 14% 96%);
    border: 1px solid hsl(220 13% 90%);
    border-radius: 10px;
    overflow-x: auto;
  }
  pre code {
    background: none;
    padding: 0;
    font-size: 13px;
    line-height: 1.5;
  }
  blockquote {
    margin: 8px 0;
    padding: 2px 0 2px 12px;
    border-left: 3px solid hsl(220 13% 85%);
    color: hsl(220 9% 46%);
  }
  table {
    margin: 8px 0;
    border-collapse: collapse;
    width: auto;
    max-width: 100%;
    font-size: 0.95em;
    display: block;
    overflow-x: auto;
  }
  th,
  td {
    border: 1px solid hsl(220 13% 88%);
    padding: 6px 12px;
    text-align: left;
    vertical-align: top;
  }
  thead th {
    background: hsl(220 14% 96%);
    font-weight: 600;
  }
  tbody tr:nth-child(even) td {
    background: hsl(220 14% 98%);
  }
`;

// Memoized: the streaming bubble re-renders on every flushed token batch, so re-parsing the whole
// (growing) string each time would be O(n²) over the reply length. useMemo skips the re-parse when
// `text` is unchanged, and React.memo skips the render entirely when a sibling state change re-renders
// the transcript without touching this bubble's text.
export const Markdown = React.memo(({ text }: { text: string }) => {
  const nodes = React.useMemo(() => parseMarkdown(text), [text]);
  return <MarkdownRoot>{nodes}</MarkdownRoot>;
});
Markdown.displayName = 'Markdown';
